import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/bits";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { Gem } from "lucide-react";

const PALETTE = ["#0B6E7A", "#12939E", "#8FD7DC", "#C1502F", "#FF7A59", "#D8C9AE"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/values-analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;
  const { values, total_opportunities } = data;

  return (
    <div data-testid="admin-analytics-page" className="space-y-6">
      <PageHeader title="تحليل القيم والأثر" sub="نحو تطوّع مدرسي يبني القيم ويقيس الأثر — توزيع الفرص على القيم الست ونِسب تحقق الأثر" />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {values.map((v, i) => (
          <div key={v.key} data-testid={`analytics-value-${v.key}`}
            className="bg-surface rounded-2xl border border-brd shadow-md p-4 text-center">
            <div className="w-9 h-9 mx-auto rounded-xl flex items-center justify-center mb-2 text-white"
              style={{ background: PALETTE[i % PALETTE.length] }}>
              <Gem className="w-4 h-4" />
            </div>
            <div className="font-head font-bold text-[13px] mb-1">{v.name}</div>
            <div className="font-head font-black text-2xl text-turq-900 dark:text-turq-300">{v.opportunities}</div>
            <div className="text-[10px] text-mutext mb-2">فرصة · {v.participants} مشارك · {v.hours} ساعة</div>
            <div className="h-1.5 rounded-full bg-surface2 overflow-hidden">
              <div className="h-full rounded-full bg-coral-600 transition-all duration-700" style={{ width: `${v.realization_pct}%` }} />
            </div>
            <div className="text-[9.5px] text-mutext mt-1">تحقق الأثر {v.realization_pct}%</div>
          </div>
        ))}
      </div>

      {total_opportunities === 0 ? (
        <EmptyState testid="analytics-empty" text="لا توجد فرص بعد لتحليلها" />
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-surface rounded-3xl p-6 shadow-md border border-brd/50">
            <h3 className="font-head font-bold text-sm mb-4">توزيع الفرص على القيم (من أصل {total_opportunities} فرصة)</h3>
            <div className="h-72" data-testid="values-pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={values} dataKey="opportunities" nameKey="name" innerRadius={55} outerRadius={95}
                    paddingAngle={3} label={({ name, share_pct }) => `${name} ${share_pct}%`}
                    labelLine={false} fontSize={11}>
                    {values.map((v, i) => <Cell key={v.key} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(val, name) => [`${val} فرصة`, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-surface rounded-3xl p-6 shadow-md border border-brd/50">
            <h3 className="font-head font-bold text-sm mb-4">نسبة تحقق الأثر لكل قيمة (فرص وُثّقت بمشاركين)</h3>
            <div className="h-72" data-testid="realization-bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={values} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--brd)" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "var(--mutext)" }} unit="%" />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: "var(--mutext)" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(val) => [`${val}%`, "تحقق الأثر"]} />
                  <Legend formatter={() => "نسبة تحقق الأثر"} />
                  <Bar dataKey="realization_pct" fill="#FF7A59" radius={[0, 6, 6, 0]} name="تحقق الأثر" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-3xl p-6 shadow-md border border-brd/50">
        <h3 className="font-head font-bold text-sm mb-4">تعريفات القيم الست المعتمدة</h3>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {values.map((v, i) => (
            <div key={v.key} className="rounded-2xl bg-bg p-4 border-r-4" style={{ borderRightColor: PALETTE[i % PALETTE.length] }}>
              <div className="font-head font-bold text-[13.5px] mb-1.5">{v.name}</div>
              <p className="text-[11.5px] text-mutext leading-relaxed">{v.definition}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
